// Subscription & Billing Policy for CareerOS-AI.
import { Link } from "react-router-dom";
import LegalLayout, { bullets } from "./LegalLayout";

const EFFECTIVE = "July 16, 2026";

const SECTIONS = [
  { n: 1, id: "plans", title: "Plans we offer",
    body: (<>
      <p>CareerOS-AI is available on the following plans:</p>
      {bullets([
        "Free — access to core tools with usage limits and standard AI models.",
        "Pro — expanded limits and access to premium AI features.",
        "Premium — full access to every feature, including executive-tier tools and advanced financial simulations.",
      ])}
      <p>Current plan features and prices are always shown on the <Link to="/app/pricing">Pricing page</Link>.</p>
    </>) },
  { n: 2, id: "billing-cycles", title: "Billing cycles",
    body: (<>
      <p>Paid plans are offered on monthly and annual billing cycles.</p>
      <p>Monthly plans renew every calendar month on the day matching your original subscription date. Annual plans renew every 12 months on the anniversary.</p>
      <p>All charges are processed by Stripe. Prices are shown in your local currency where available; otherwise USD.</p>
    </>) },
  { n: 3, id: "auto-renewal", title: "Automatic renewal",
    body: (<>
      <p>Subscriptions renew automatically at the end of each billing cycle unless you cancel before that date.</p>
      <p>You will receive a receipt by email after each successful charge. Where required by law, we will also send a renewal reminder before the charge occurs.</p>
    </>) },
  { n: 4, id: "upgrades-downgrades", title: "Upgrades and downgrades",
    body: (<>
      <p><strong>Upgrade</strong> — takes effect immediately. We prorate the remainder of your current cycle and add it to your new plan.</p>
      <p><strong>Downgrade</strong> — takes effect at the end of your current billing cycle so you receive the value you already paid for. No proration is issued.</p>
    </>) },
  { n: 5, id: "trials-and-promos", title: "Trials and promotional offers",
    body: (<>
      <p>If we offer a free trial or promotional discount, its terms and duration will be shown at the point of sign-up.</p>
      <p>Unless otherwise stated:</p>
      {bullets([
        "You may only use one trial per person or household.",
        "Trials automatically convert to a paid subscription at the end of the trial period unless you cancel.",
        "Promotional pricing applies to the promotional period only; standard pricing applies on renewal.",
      ])}
    </>) },
  { n: 6, id: "cancellation", title: "Cancellation",
    body: (<>
      <p>You can cancel at any time from <Link to="/app/settings">Settings → Subscription</Link>.</p>
      <p>Cancellation:</p>
      {bullets([
        "Stops future charges immediately.",
        "Keeps your paid features active until the end of the current billing cycle.",
        "Does not automatically refund past payments — see our Refund Policy.",
        "Preserves your account and data — you can resubscribe or return to Free at any time.",
      ])}
    </>) },
  { n: 7, id: "failed-payments", title: "Failed payments",
    body: (<>
      <p>If a charge fails (expired card, insufficient funds, bank decline, etc.):</p>
      {bullets([
        "We retry the charge over the following days according to Stripe's smart-retry schedule.",
        "We email you with instructions to update your payment method.",
        "If payment is not resolved, premium features are suspended and your account is placed on the Free plan.",
        "Your account and data are not deleted.",
      ])}
    </>) },
  { n: 8, id: "price-changes", title: "Price changes",
    body: (<>
      <p>We may change plan prices. If we do, existing subscribers will:</p>
      {bullets([
        "Continue at their existing price for the remainder of the current committed period (e.g. the rest of the annual term).",
        "Receive at least 30 days&apos; email notice before the price change takes effect on their next renewal.",
        "Have the opportunity to cancel before the change applies.",
      ])}
    </>) },
  { n: 9, id: "taxes", title: "Taxes",
    body: (<>
      <p>Prices displayed on the Pricing page may exclude applicable sales tax, VAT, GST/HST, or equivalent. Where CareerOS-AI is required to collect these taxes, they are added at checkout.</p>
      <p>If you are a business subject to reverse-charge in your jurisdiction, you may enter a valid tax ID at checkout.</p>
    </>) },
  { n: 10, id: "invoices", title: "Receipts and invoices",
    body: (<>
      <p>After each successful charge you will receive a PDF receipt by email. Business invoices, including your legal name and tax ID, can be requested from <span className="font-mono">support@careeros-ai.com</span>.</p>
    </>) },
  { n: 11, id: "refunds", title: "Refunds",
    body: <p>Refund eligibility, timing, and process are covered in our dedicated <Link to="/refund-policy">Refund Policy</Link>.</p> },
  { n: 12, id: "chargebacks", title: "Chargebacks and disputes",
    body: (<>
      <p>Please contact us before initiating a chargeback with your bank. Nine out of ten disputes are misunderstandings we can resolve within a business day.</p>
      <p>Unresolved chargebacks may result in immediate account suspension and forfeiture of any account credit while the dispute is under investigation.</p>
    </>) },
  { n: 13, id: "changes", title: "Changes to this policy",
    body: <p>We may update this policy. Material changes are communicated on the website or by email. The &quot;Last Updated&quot; date reflects the current version.</p> },
  { n: 14, id: "contact", title: "Contact",
    body: <p>Billing questions: <span className="font-mono">support@careeros-ai.com</span>.</p> },
];

const INTRO = (
  <p>This Subscription &amp; Billing Policy explains how paid CareerOS-AI plans work, how you are charged, and how to manage or cancel your subscription. Read together with our <Link to="/terms">Terms</Link> and <Link to="/refund-policy">Refund Policy</Link>.</p>
);

export default function SubscriptionPolicy() {
  return (
    <LegalLayout
      eyebrow="Legal · Subscription"
      title="Subscription & Billing Policy"
      subtitle="for CareerOS-AI"
      effectiveDate={EFFECTIVE}
      lastUpdated={EFFECTIVE}
      intro={INTRO}
      sections={SECTIONS}
      currentPath="/subscription-policy"
    />
  );
}
